import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { worker as workerService } from "services/api/company/worker.service";
import type { CompanyWorker } from "model/Company";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  getCachedPersonOptions,
  PersonOption,
  setCachedPersonOptions,
} from "./companyFilterOptions.cache";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";

interface UseCompanyFilterOptionsParams {
  companyId: number | undefined;
  setSelectedWorkGroupIds?: Dispatch<SetStateAction<string[]>>;
  loadPersonOptions?: boolean;
}

interface UseCompanyFilterOptionsState {
  unitTeamOptions: UnitTeamOption[];
  personOptions: PersonOption[];
  workersLoadError: boolean;
  unitTeamLoadError: boolean;
}

const personOptionsInFlight = new Map<
  number,
  Promise<{ options: PersonOption[]; hasError: boolean }>
>();
const isUnhandledName = (name: string | null | undefined): boolean => {
  return (name ?? "").trim().toLowerCase() === "unhandled";
};

const toPersonOptions = (workers: CompanyWorker[]): PersonOption[] => {
  return workers.map((worker) => {
    const label = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
    return {
      value: String(worker.id),
      label: label.length > 0 ? label : String(worker.id),
    };
  });
};

const loadPersonOptions = async (
  companyId: number,
): Promise<{ options: PersonOption[]; hasError: boolean }> => {
  const cached = getCachedPersonOptions(companyId);
  if (cached) {
    return { options: cached, hasError: false };
  }

  const inFlight = personOptionsInFlight.get(companyId);
  if (inFlight) {
    return inFlight;
  }

  const request = workerService
    .getCompanyWorkers(companyId, false)
    .then((response) => {
      const mappedOptions = toPersonOptions(response.data);
      setCachedPersonOptions(companyId, mappedOptions);
      return { options: mappedOptions, hasError: false };
    })
    .catch(() => ({ options: [], hasError: true }))
    .finally(() => {
      personOptionsInFlight.delete(companyId);
    });

  personOptionsInFlight.set(companyId, request);
  return request;
};

/**
 * Loads and maps company filter data used by toolbar controls.
 * Returns unit/team options and person options for the provided company.
 * Optionally keeps selected work-group ids in sync with currently available teams.
 */
export const useCompanyFilterOptions = (
  params: UseCompanyFilterOptionsParams,
): UseCompanyFilterOptionsState => {
  const companyId = params.companyId;
  const setSelectedWorkGroupIds = params.setSelectedWorkGroupIds;
  const loadPersonOptionsOnMount = params.loadPersonOptions ?? true;
  const [organizationalStructureState] = useOrganizationalStructureStore();
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [workersLoadError, setWorkersLoadError] = useState(false);
  const unitTeamLoadError = organizationalStructureState.error !== null;

  const unitTeamOptions = useMemo<UnitTeamOption[]>(() => {
    return organizationalStructureState.structure.entities
      .flatMap((entity) => {
        return (entity.units ?? []).map((unit) => ({
          unitId: unit.id,
          unitName: unit.name,
          teams: (unit.workGroups ?? []).map((team) => ({
            id: team.id,
            name: team.name,
          })),
        }));
      })
      .filter((unit) => !isUnhandledName(unit.unitName))
      .map((unit) => ({
        ...unit,
        teams: unit.teams.filter((team) => !isUnhandledName(team.name)),
      }))
      .filter((unit) => unit.teams.length > 0);
  }, [organizationalStructureState.structure.entities]);

  useEffect(() => {
    if (!loadPersonOptionsOnMount) {
      setPersonOptions([]);
      setWorkersLoadError(false);
      return;
    }
    if (!companyId) {
      setPersonOptions([]);
      setWorkersLoadError(false);
      return;
    }
    let isMounted = true;
    void loadPersonOptions(companyId).then(({ options, hasError }) => {
      if (!isMounted) return;
      setPersonOptions(options);
      setWorkersLoadError(hasError);
    });
    return () => {
      isMounted = false;
    };
  }, [companyId, loadPersonOptionsOnMount]);

  useEffect(() => {
    if (!setSelectedWorkGroupIds) return;
    const availableIds = new Set(
      unitTeamOptions.flatMap((unit) =>
        unit.teams.map((team) => String(team.id)),
      ),
    );
    setSelectedWorkGroupIds((prev) => {
      const next = prev.filter((value) => availableIds.has(value));
      const hasSameLength = next.length === prev.length;
      const hasSameValues =
        hasSameLength && next.every((value, index) => value === prev[index]);
      return hasSameValues ? prev : next;
    });
  }, [setSelectedWorkGroupIds, unitTeamOptions]);

  return {
    unitTeamOptions,
    personOptions,
    workersLoadError,
    unitTeamLoadError,
  };
};
